import axios from 'axios'
import { create } from 'zustand'
import {
  operatorApi,
  type OperatorApprovalResultDto,
  type OperatorPackageProjectionDto,
  type OperatorPackageResultDto,
  type OperatorPolicyDto,
  type OperatorProjectDto,
  type OperatorProposalDto,
  type OperatorProposalQuery,
  type OperatorReviewPackageDto,
  type OperatorTimelineDto,
  type OperatorZoom,
} from '../services/operatorApi'

export interface OperatorFilters {
  organizations: string[]
  teams: string[]
  people: string[]
  eventTypes: string[]
  statuses: string[]
  horizon: 'all' | 'past' | 'future'
}

interface OperatorLoadingState {
  proposals: boolean
  package: boolean
  policy: boolean
  projects: boolean
  timeline: boolean
}

interface OperatorErrorState {
  proposals: string | null
  package: string | null
  policy: string | null
  projects: string | null
  timeline: string | null
  approval: string | null
  rejection: string | null
  retry: string | null
}

interface OperatorState {
  currentWorkspaceId: string | null
  proposals: OperatorProposalDto[]
  policy: OperatorPolicyDto | null
  projects: OperatorProjectDto[]
  timeline: OperatorTimelineDto | null
  proposalPackages: Record<string, OperatorReviewPackageDto>
  proposalResults: Record<string, OperatorPackageProjectionDto>
  filters: OperatorFilters
  zoom: OperatorZoom
  loading: OperatorLoadingState
  errors: OperatorErrorState
  approvingProposalId: string | null
  rejectingProposalId: string | null
  retryingActionId: string | null
  activateWorkspace: (workspaceId: string | null) => void
  loadProposals: (query?: OperatorProposalQuery) => Promise<void>
  loadProposalPackage: (proposalId: string) => Promise<void>
  loadPolicy: () => Promise<void>
  loadProjects: () => Promise<void>
  loadTimeline: (projectBindingId: string) => Promise<void>
  approveProposal: (proposalId: string) => Promise<OperatorApprovalResultDto>
  retryAction: (
    proposalId: string,
    actionId: string,
  ) => Promise<OperatorPackageResultDto | null>
  rejectProposal: (proposalId: string) => Promise<void>
  setFilter: <K extends keyof OperatorFilters>(
    key: K,
    value: OperatorFilters[K],
  ) => void
  setZoom: (zoom: OperatorZoom) => void
  reset: () => void
}

const initialFilters: OperatorFilters = {
  organizations: [],
  teams: [],
  people: [],
  eventTypes: [],
  statuses: [],
  horizon: 'all',
}

const initialLoading: OperatorLoadingState = {
  proposals: false,
  package: false,
  policy: false,
  projects: false,
  timeline: false,
}

const initialErrors: OperatorErrorState = {
  proposals: null,
  package: null,
  policy: null,
  projects: null,
  timeline: null,
  approval: null,
  rejection: null,
  retry: null,
}

const initialState = {
  currentWorkspaceId: null as string | null,
  proposals: [] as OperatorProposalDto[],
  policy: null as OperatorPolicyDto | null,
  projects: [] as OperatorProjectDto[],
  timeline: null as OperatorTimelineDto | null,
  proposalPackages: {} as Record<string, OperatorReviewPackageDto>,
  proposalResults: {} as Record<string, OperatorPackageProjectionDto>,
  filters: initialFilters,
  zoom: 'week' as OperatorZoom,
  loading: initialLoading,
  errors: initialErrors,
  approvingProposalId: null as string | null,
  rejectingProposalId: null as string | null,
  retryingActionId: null as string | null,
}

const approvalRequests = new Map<string, Promise<OperatorApprovalResultDto>>()
const retryRequests = new Map<
  string,
  Promise<OperatorPackageResultDto | null>
>()
const rejectionRequests = new Map<string, Promise<void>>()
const packageRequests = new Map<string, number>()
let workspaceGeneration = 0
let proposalsRequest = 0
let packageRequestSequence = 0
let policyRequest = 0
let projectsRequest = 0
let timelineRequest = 0

function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message
    if (typeof message === 'string' && message.trim()) return message
  }
  return error instanceof Error && error.message.trim()
    ? error.message
    : 'The operator request could not be completed.'
}

function proposalStateForPackage(
  state: OperatorPackageProjectionDto['state'],
): OperatorProposalDto['state'] {
  if (state === 'applied') return 'applied'
  if (state === 'failed' || state === 'denied') return 'rejected'
  return 'approved'
}

export const useOperatorStore = create<OperatorState>((set, get) => ({
  ...initialState,

  activateWorkspace: (workspaceId) => {
    if (get().currentWorkspaceId === workspaceId) return

    workspaceGeneration += 1
    approvalRequests.clear()
    retryRequests.clear()
    rejectionRequests.clear()
    packageRequests.clear()
    proposalsRequest += 1
    policyRequest += 1
    projectsRequest += 1
    timelineRequest += 1
    set({
      currentWorkspaceId: workspaceId,
      proposals: [],
      policy: null,
      projects: [],
      timeline: null,
      proposalPackages: {},
      proposalResults: {},
      filters: { ...initialFilters },
      zoom: initialState.zoom,
      loading: { ...initialLoading },
      errors: { ...initialErrors },
      approvingProposalId: null,
      rejectingProposalId: null,
      retryingActionId: null,
    })
  },

  loadProposals: async (query = {}) => {
    const request = ++proposalsRequest
    set((state) => ({
      loading: { ...state.loading, proposals: true },
      errors: { ...state.errors, proposals: null },
    }))
    try {
      const { data } = await operatorApi.listProposals(query)
      if (request !== proposalsRequest) return
      set((state) => ({
        proposals: data,
        loading: { ...state.loading, proposals: false },
      }))
    } catch (error) {
      if (request !== proposalsRequest) return
      set((state) => ({
        loading: { ...state.loading, proposals: false },
        errors: { ...state.errors, proposals: errorMessage(error) },
      }))
    }
  },

  loadProposalPackage: async (proposalId) => {
    const requestGeneration = workspaceGeneration
    const request = ++packageRequestSequence
    packageRequests.set(proposalId, request)
    set((state) => ({
      loading: { ...state.loading, package: true },
      errors: { ...state.errors, package: null },
    }))
    try {
      const { data } = await operatorApi.getProposalPackage(proposalId)
      if (
        requestGeneration !== workspaceGeneration
        || packageRequests.get(proposalId) !== request
      ) return
      packageRequests.delete(proposalId)
      set((state) => {
        const proposalResults = { ...state.proposalResults }
        if (data.executionState) {
          proposalResults[proposalId] = {
            workspaceId: data.workspaceId,
            proposalId: data.proposalId,
            state: data.executionState,
            actions: data.actionOutcomes,
          }
        } else {
          delete proposalResults[proposalId]
        }
        return {
          proposalPackages: {
            ...state.proposalPackages,
            [proposalId]: data,
          },
          proposalResults,
          loading: { ...state.loading, package: false },
        }
      })
    } catch (error) {
      if (
        requestGeneration !== workspaceGeneration
        || packageRequests.get(proposalId) !== request
      ) return
      packageRequests.delete(proposalId)
      set((state) => ({
        loading: { ...state.loading, package: false },
        errors: { ...state.errors, package: errorMessage(error) },
      }))
    }
  },

  loadPolicy: async () => {
    const request = ++policyRequest
    set((state) => ({
      loading: { ...state.loading, policy: true },
      errors: { ...state.errors, policy: null },
    }))
    try {
      const { data } = await operatorApi.getPolicy()
      if (request !== policyRequest) return
      set((state) => ({
        policy: data,
        loading: { ...state.loading, policy: false },
      }))
    } catch (error) {
      if (request !== policyRequest) return
      set((state) => ({
        loading: { ...state.loading, policy: false },
        errors: { ...state.errors, policy: errorMessage(error) },
      }))
    }
  },

  loadProjects: async () => {
    const request = ++projectsRequest
    set((state) => ({
      loading: { ...state.loading, projects: true },
      errors: { ...state.errors, projects: null },
    }))
    try {
      const { data } = await operatorApi.listProjects()
      if (request !== projectsRequest) return
      set((state) => ({
        projects: data,
        loading: { ...state.loading, projects: false },
      }))
    } catch (error) {
      if (request !== projectsRequest) return
      set((state) => ({
        loading: { ...state.loading, projects: false },
        errors: { ...state.errors, projects: errorMessage(error) },
      }))
    }
  },

  loadTimeline: async (projectBindingId) => {
    const request = ++timelineRequest
    set((state) => ({
      loading: { ...state.loading, timeline: true },
      errors: { ...state.errors, timeline: null },
    }))
    try {
      const { data } = await operatorApi.getProjectTimeline(projectBindingId)
      if (request !== timelineRequest) return
      set((state) => ({
        timeline: data,
        loading: { ...state.loading, timeline: false },
      }))
    } catch (error) {
      if (request !== timelineRequest) return
      set((state) => ({
        loading: { ...state.loading, timeline: false },
        errors: { ...state.errors, timeline: errorMessage(error) },
      }))
    }
  },

  approveProposal: (proposalId) => {
    const inFlight = approvalRequests.get(proposalId)
    if (inFlight) return inFlight
    const actionPackage = get().proposalPackages[proposalId]
    if (!actionPackage) {
      const error = new Error(
        'The reviewed package is not available. Refresh before approving.',
      )
      set((state) => ({
        errors: { ...state.errors, approval: error.message },
      }))
      return Promise.reject(error)
    }
    const requestGeneration = workspaceGeneration

    set((state) => ({
      approvingProposalId: proposalId,
      errors: { ...state.errors, approval: null },
    }))
    const request = operatorApi.approveProposal(proposalId, {
      actionPackageId: actionPackage.actionPackageId,
      packageFingerprint: actionPackage.packageFingerprint,
    })
      .then(({ data }) => {
        if (requestGeneration !== workspaceGeneration) return data
        set((state) => ({
          proposalResults: {
            ...state.proposalResults,
            [proposalId]: data,
          },
          proposals: state.proposals.map((proposal) =>
            proposal.id === proposalId
              ? {
                  ...proposal,
                  state: proposalStateForPackage(data.state),
                }
              : proposal),
        }))
        return data
      })
      .catch((error: unknown) => {
        if (requestGeneration === workspaceGeneration) {
          set((state) => ({
            errors: { ...state.errors, approval: errorMessage(error) },
          }))
        }
        throw error
      })
      .finally(() => {
        if (
          requestGeneration !== workspaceGeneration
          || approvalRequests.get(proposalId) !== request
        ) {
          return
        }
        approvalRequests.delete(proposalId)
        set((state) => ({
          approvingProposalId:
            state.approvingProposalId === proposalId
              ? null
              : state.approvingProposalId,
        }))
      })
    approvalRequests.set(proposalId, request)
    return request
  },

  retryAction: (proposalId, actionId) => {
    const inFlight = retryRequests.get(actionId)
    if (inFlight) return inFlight
    const actionPackage = get().proposalPackages[proposalId]
    const currentAction = get().proposalResults[proposalId]?.actions.find(
      (action) => action.actionId === actionId,
    )
    if (!actionPackage || !currentAction?.retryable) {
      const error = new Error(
        'The retryable action reference is not available. Refresh before retrying.',
      )
      set((state) => ({
        errors: { ...state.errors, retry: error.message },
      }))
      return Promise.reject(error)
    }
    const requestGeneration = workspaceGeneration
    set((state) => ({
      retryingActionId: actionId,
      errors: { ...state.errors, retry: null },
    }))
    const request = operatorApi.retryAction(actionId, {
      actionPackageId: actionPackage.actionPackageId,
      packageFingerprint: actionPackage.packageFingerprint,
    })
      .then(({ data }) => {
        if (requestGeneration !== workspaceGeneration) return null
        set((state) => ({
          proposalResults: {
            ...state.proposalResults,
            [proposalId]: data,
          },
          proposals: state.proposals.map((proposal) =>
            proposal.id === proposalId
              ? { ...proposal, state: proposalStateForPackage(data.state) }
              : proposal),
        }))
        return data
      })
      .catch((error: unknown) => {
        if (requestGeneration !== workspaceGeneration) return null
        set((state) => ({
          errors: { ...state.errors, retry: errorMessage(error) },
        }))
        throw error
      })
      .finally(() => {
        if (
          requestGeneration !== workspaceGeneration
          || retryRequests.get(actionId) !== request
        ) {
          return
        }
        retryRequests.delete(actionId)
        set((state) => ({
          retryingActionId:
            state.retryingActionId === actionId
              ? null
              : state.retryingActionId,
        }))
      })
    retryRequests.set(actionId, request)
    return request
  },

  rejectProposal: (proposalId) => {
    const inFlight = rejectionRequests.get(proposalId)
    if (inFlight) return inFlight
    const requestGeneration = workspaceGeneration
    set((state) => ({
      rejectingProposalId: proposalId,
      errors: { ...state.errors, rejection: null },
    }))
    const request = operatorApi.rejectProposal(proposalId)
      .then(({ data }) => {
        if (requestGeneration !== workspaceGeneration) return
        set((state) => ({
          proposals: state.proposals.map((proposal) =>
            proposal.id === proposalId
              ? { ...proposal, ...data, evidenceLinks: proposal.evidenceLinks }
              : proposal),
        }))
      })
      .catch((error: unknown) => {
        if (requestGeneration === workspaceGeneration) {
          set((state) => ({
            errors: { ...state.errors, rejection: errorMessage(error) },
          }))
        }
        throw error
      })
      .finally(() => {
        if (
          requestGeneration !== workspaceGeneration
          || rejectionRequests.get(proposalId) !== request
        ) {
          return
        }
        rejectionRequests.delete(proposalId)
        set((state) => ({
          rejectingProposalId:
            state.rejectingProposalId === proposalId
              ? null
              : state.rejectingProposalId,
        }))
      })
    rejectionRequests.set(proposalId, request)
    return request
  },

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }))
  },

  setZoom: (zoom) => set({ zoom }),

  reset: () => {
    workspaceGeneration += 1
    approvalRequests.clear()
    retryRequests.clear()
    rejectionRequests.clear()
    packageRequests.clear()
    proposalsRequest += 1
    policyRequest += 1
    projectsRequest += 1
    timelineRequest += 1
    set({
      ...initialState,
      filters: { ...initialFilters },
      loading: { ...initialLoading },
      errors: { ...initialErrors },
      proposalPackages: {},
      proposalResults: {},
    })
  },
}))
